import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { UsuarioSesion } from '@doc/shared';
import { api, tokens } from './api.js';

interface Contexto {
  usuario: UsuarioSesion | null;
  cargando: boolean;
  entrar(email: string, password: string): Promise<void>;
  salir(): Promise<void>;
  esAdmin: boolean;
}

const ContextoSesion = createContext<Contexto | null>(null);

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioSesion | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    // Al recargar la página el token sigue en localStorage: se valida contra
    // la API en vez de confiar en lo que haya guardado el navegador.
    if (!tokens.access()) {
      setCargando(false);
      return;
    }
    api<UsuarioSesion>('/auth/me')
      .then(setUsuario)
      .catch(() => tokens.limpiar())
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => {
    const alCaer = () => setUsuario(null);
    window.addEventListener('sesion-caida', alCaer);
    return () => window.removeEventListener('sesion-caida', alCaer);
  }, []);

  const entrar = useCallback(async (email: string, password: string) => {
    const datos = await api<{ access: string; refresh: string; usuario: UsuarioSesion }>('/auth/login', {
      metodo: 'POST',
      cuerpo: { email, password },
    });
    tokens.guardar(datos.access, datos.refresh);
    setUsuario(datos.usuario);
  }, []);

  const salir = useCallback(async () => {
    await api('/auth/logout', { metodo: 'POST' }).catch(() => undefined);
    tokens.limpiar();
    setUsuario(null);
  }, []);

  const valor = useMemo<Contexto>(
    () => ({
      usuario,
      cargando,
      entrar,
      salir,
      esAdmin: usuario?.rol === 'ADMIN' || usuario?.rol === 'SUPER_ADMIN',
    }),
    [usuario, cargando, entrar, salir],
  );

  return <ContextoSesion.Provider value={valor}>{children}</ContextoSesion.Provider>;
}

export function useSesion(): Contexto {
  const contexto = useContext(ContextoSesion);
  if (!contexto) throw new Error('useSesion fuera del proveedor');
  return contexto;
}
